/**
 * `<WorkspaceSwitchedBanner>` — why the page just changed under you (#803).
 *
 * One sign-in is shared by every open tab, so switching workspace in one tab
 * (or in another Mangrove app) switches them all. A tab still showing the old
 * workspace catches up by reloading, and anything it tried to save for the old
 * workspace was refused, not saved in the new one. This says so, once, after
 * that reload. Presentational: the app shows it when
 * `useWorkspaceSwitchedElsewhere()` (@mangrove/trpc-client/react) says to.
 */
import * as React from 'react';
import { X } from 'lucide-react';

export interface WorkspaceSwitchedBannerProps {
  onDismiss: () => void;
}

export function WorkspaceSwitchedBanner({
  onDismiss,
}: WorkspaceSwitchedBannerProps): React.JSX.Element {
  return (
    <div
      role="status"
      data-testid="workspace-switched-banner"
      className="sticky top-0 z-50 flex items-start justify-between gap-3 border-b border-sky-300 bg-sky-50 px-4 py-2 text-sm text-sky-950 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100"
    >
      <p>
        <strong>This tab now shows the workspace you are in.</strong> It was switched in another tab
        or app, so the page reloaded. Anything this tab tried to save for the other workspace was
        not saved.
      </p>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 rounded p-0.5 hover:bg-sky-100 dark:hover:bg-sky-900"
        aria-label="Dismiss"
        data-testid="workspace-switched-dismiss"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
